import React from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import { X, Loader2 } from 'lucide-react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/Tabs';
import { SignInForm } from './SignInForm';
import { SignUpForm } from './SignUpForm';
import { AuthSuccess } from './AuthSuccess';
import { useUnifiedAuth } from '@/lib/auth/use-unified-auth';
import { useLocation } from 'react-router-dom';
import { useModalStore } from '@/lib/stores/modal-store';
import { logger } from '@/lib/utils/logger';

interface AuthModalProps {
  isOpen: boolean;
  onClose: () => void;
  defaultTab?: 'sign-in' | 'sign-up';
  redirectTo?: string;
}

export function AuthModal({ 
  isOpen, 
  onClose, 
  defaultTab = 'sign-in', 
  redirectTo 
}: AuthModalProps) {
  const { error, isLoading } = useUnifiedAuth();
  const { authModal, showAuthSuccess } = useModalStore();
  const [activeTab, setActiveTab] = React.useState<'sign-in' | 'sign-up'>(defaultTab);
  const location = useLocation();
  const successTimeoutRef = React.useRef<NodeJS.Timeout | null>(null);
  const wasOpenRef = React.useRef(false);
  const [isTransitioning, setIsTransitioning] = React.useState(false);
  const [pendingSuccess, setPendingSuccess] = React.useState(false);
  const [showForm, setShowForm] = React.useState(false);

  // Use global modal store success state
  const showSuccess = authModal.showSuccess;
  const successMessage = authModal.successMessage || '';

  // Store current path for redirect
  React.useEffect(() => {
    if (isOpen) {
      const currentPath = location.pathname + location.search;
      window.sessionStorage.setItem('authRedirectPath', redirectTo || currentPath);
    }
  }, [isOpen, location, redirectTo]);

  // Reset state when modal opens/closes
  React.useEffect(() => {
    if (isOpen) {
      setActiveTab(defaultTab);
      
      // Only reset if this is a fresh modal open (not already open)
      if (!wasOpenRef.current) {
        // Don't show form immediately
        setShowForm(false);
      }
      
      wasOpenRef.current = true;
      
      // If modal opens and not loading/success, show form after brief delay
      if (!isLoading && !showSuccess) {
        setTimeout(() => {
          setShowForm(true);
        }, 100);
      }
    } else {
      wasOpenRef.current = false;
      setShowForm(false);
      
      // Clear success state when modal closes
      if (successTimeoutRef.current) {
        clearTimeout(successTimeoutRef.current);
        successTimeoutRef.current = null;
      }
    }
  }, [isOpen, defaultTab, isLoading, showSuccess]);

  // Track loading state changes to handle transitions
  React.useEffect(() => {
    if (isLoading) {
      setIsTransitioning(true);
      setPendingSuccess(false);
      setShowForm(false);
    } else if (isTransitioning && !isLoading && !showSuccess && !error) {
      // Loading just finished without error - assume success is pending
      setPendingSuccess(true);
      // Keep transitioning state active until we get success or timeout
      const timer = setTimeout(() => {
        setIsTransitioning(false);
        setPendingSuccess(false);
        // Only show form if still no success after timeout
        if (!showSuccess) {
          setShowForm(true);
        }
      }, 500); // Give more time for success state to be set
      return () => clearTimeout(timer);
    } else if (showSuccess) {
      setIsTransitioning(false);
      setPendingSuccess(false);
      setShowForm(false);
    } else if (error) {
      setIsTransitioning(false);
      setPendingSuccess(false);
      setShowForm(true);
    }
  }, [isLoading, showSuccess, isTransitioning, error]);

  // Handle successful authentication
  const handleSuccess = React.useCallback((message: string) => {
    logger.info('AuthModal handleSuccess called', { message, hasError: !!error });
    
    // Clear any existing timeout
    if (successTimeoutRef.current) {
      clearTimeout(successTimeoutRef.current);
      successTimeoutRef.current = null;
    }
    
    // Set pending success before showing success
    setPendingSuccess(false);
    
    // Show success state in global modal store
    showAuthSuccess(message);
  }, [showAuthSuccess, error]);

  // Handle success timeout in a separate effect
  React.useEffect(() => {
    if (showSuccess) {
      successTimeoutRef.current = setTimeout(() => {
        onClose();
      }, 3000);

      return () => {
        if (successTimeoutRef.current) {
          clearTimeout(successTimeoutRef.current);
          successTimeoutRef.current = null;
        }
      };
    }
  }, [showSuccess, onClose]);

  // Get appropriate header text based on current state
  const getHeaderText = () => {
    if (showSuccess) return 'Success!';
    return activeTab === 'sign-up' ? 'Create Account' : 'Welcome Back';
  };

  return (
    <Dialog.Root open={isOpen} onOpenChange={showSuccess ? undefined : onClose}>
      <Dialog.Portal>
        <Dialog.Overlay 
          className="fixed inset-0 bg-earth-900/80 backdrop-blur-sm z-40"
        />
        <Dialog.Content className="fixed left-[50%] top-[50%] translate-x-[-50%] translate-y-[-50%] w-full max-w-md bg-earth-800 rounded-lg shadow-xl z-50 flex flex-col max-h-[90vh]">
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b border-earth-700">
            <Dialog.Title className="text-2xl font-display text-sand-100">
              {getHeaderText()}
            </Dialog.Title>
            <Dialog.Close 
              className="text-sand-400 hover:text-sand-300"
              disabled={showSuccess}
            >
              <X className="h-5 w-5" />
            </Dialog.Close>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto">
            {(() => {
              if (showSuccess) {
                return (
                  <AuthSuccess 
                    message={successMessage}
                    submessage={activeTab === 'sign-up' ? 
                      "Your account has been created successfully." :
                      "You're now logged in to your account."
                    }
                  />
                );
              } else if (isLoading || isTransitioning || pendingSuccess || !showForm) {
                return (
                  <div className="p-6 flex flex-col items-center justify-center">
                    <Loader2 className="h-8 w-8 text-sand-400 animate-spin mb-4" />
                    <p className="text-sand-300">
                      {pendingSuccess ? 'Success! Preparing your dashboard...' : 'Authenticating...'}
                    </p>
                  </div>
                );
              } else {
                return (
                  <div className="p-6">
                    <Tabs 
                      value={activeTab}
                      className="space-y-6"
                      onValueChange={(value) => {
                        setActiveTab(value as 'sign-in' | 'sign-up');
                      }}
                    >
                      <TabsList className="w-full">
                        <TabsTrigger 
                          value="sign-in" 
                          className="flex-1 data-[state=active]:bg-sand-200 data-[state=active]:text-earth-900"
                        >
                          Login
                        </TabsTrigger>
                        <TabsTrigger 
                          value="sign-up" 
                          className="flex-1 data-[state=active]:bg-sand-200 data-[state=active]:text-earth-900"
                        >
                          Create Account
                        </TabsTrigger>
                      </TabsList>

                      <TabsContent value="sign-in">
                        <SignInForm 
                          onSuccess={() => handleSuccess("Welcome back!")}
                          redirectTo={redirectTo}
                        />
                      </TabsContent>

                      <TabsContent value="sign-up">
                        <SignUpForm 
                          onSuccess={() => handleSuccess("Account created successfully!")}
                          redirectTo={redirectTo}
                        />
                      </TabsContent>
                    </Tabs>
                  </div>
                );
              }
            })()}
          </div>

          {error && !isLoading && !showSuccess && (
            <div className="p-4 border-t border-earth-700 bg-red-500/10">
              <p className="text-sm text-red-500 text-center">{error.message || error}</p>
            </div>
          )}
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}